import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AuthUser, LoginRequest, UserRole, RolePermissions } from '../types'
import { authApi } from '../services/authApi'

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  selectedTenantId: string | null // For pentester tenant filtering
}

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  clearError: () => void
  setLoading: (loading: boolean) => void
  initializeAuth: () => void
  getUserRole: () => UserRole | null
  getUserPermissions: () => RolePermissions
  setSelectedTenantId: (tenantId: string | null) => void
}

type AuthStore = AuthState & AuthActions

// Role-based permissions configuration
const getRolePermissions = (role: UserRole): RolePermissions => {
  switch (role) {
    case 'ADMIN':
      return {
        canViewDashboard: true,
        canManageUsers: true,
        canManageTenants: true,
        canManageProjects: true,
        canViewVulnerabilities: true,
        canManageVulnerabilities: true,
        canRunScans: true,
        canViewReports: true,
        canManageSettings: true,
        canViewAssignedAssets: false,
        canViewAssignedProjects: false,
      }
    case 'TENANT_ADMIN':
      return {
        canViewDashboard: true,
        canManageUsers: true, // Can manage its users
        canManageTenants: false,
        canManageProjects: true, // Can manage its projects
        canViewVulnerabilities: true,
        canManageVulnerabilities: false,
        canRunScans: false,
        canViewReports: true,
        canManageSettings: true,
        canViewAssignedAssets: false,
        canViewAssignedProjects: false,
      }
    case 'TENANT_USER':
      return {
        canViewDashboard: true,
        canManageUsers: false,
        canManageTenants: false,
        canManageProjects: false,
        canViewVulnerabilities: false,
        canManageVulnerabilities: false,
        canRunScans: false,
        canViewReports: false,
        canManageSettings: false,
        canViewAssignedAssets: true, // Can view assigned assets
        canViewAssignedProjects: false,
      }
    case 'PENTESTER':
      return {
        canViewDashboard: true,
        canManageUsers: false,
        canManageTenants: false,
        canManageProjects: false,
        canViewVulnerabilities: true,
        canManageVulnerabilities: true,
        canRunScans: true,
        canViewReports: true,
        canManageSettings: false,
        canViewAssignedAssets: false,
        canViewAssignedProjects: true, // Can view assigned projects
      }
    default:
      return {
        canViewDashboard: false,
        canManageUsers: false,
        canManageTenants: false,
        canManageProjects: false,
        canViewVulnerabilities: false,
        canManageVulnerabilities: false,
        canRunScans: false,
        canViewReports: false,
        canManageSettings: false,
        canViewAssignedAssets: false,
        canViewAssignedProjects: false,
      }
  }
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      selectedTenantId: null,

      // Actions
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await authApi.login(credentials)
          
          if (response.success && (response.data as any)?.user) {
            const userData = (response.data as any).user
            
            set({
              user: userData,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            })
          } else {
            throw new Error(response.message || 'Login failed')
          }
        } catch (error: any) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.message || 'Login failed. Please try again.',
          })
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })
        
        try {
          await authApi.logout()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          console.error('Logout error:', error)
          // Even if logout fails on server, clear local state
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      refreshToken: async () => {
        try {
          await authApi.refreshToken()
          // Update the user data from localStorage after token refresh
          const user = authApi.getCurrentUser()
          set({ user })
        } catch (error: any) {
          console.error('Token refresh error:', error)
          // If refresh fails, logout user
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: 'Session expired. Please login again.',
          })
          throw error
        }
      },

      clearError: () => {
        set({ error: null })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      initializeAuth: () => {
        const user = authApi.getCurrentUser()
        const isAuthenticated = authApi.isAuthenticated()
        
        set({
          user,
          isAuthenticated,
          isLoading: false,
          error: null,
        })
      },

      getUserRole: (): UserRole | null => {
        const { user } = get()
        
        if (!user || !user.roles || user.roles.length === 0) {
          return null
        }
        
        // Return the first role (assuming single role per user)
        return user.roles[0] as UserRole
      },

      getUserPermissions: (): RolePermissions => {
        const role = get().getUserRole()
        return getRolePermissions(role || 'TENANT_ADMIN') // Default to TENANT_ADMIN if no role
      },

      setSelectedTenantId: (tenantId: string | null) => {
        set({ selectedTenantId: tenantId })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        selectedTenantId: state.selectedTenantId,
      }),
    }
  )
)

// Initialize auth state on app start
export const initializeAuth = () => {
  useAuthStore.getState().initializeAuth()
}
